import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, ArrowLeft, Edit, Save, Shield, DollarSign, Coins
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

export default function AdminLeadPricing() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/admin/lead-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data);
    } catch (error) {
      toast.error("Failed to load pricing rules");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule({ ...rule });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingRule) return;
    
    setSaving(true);
    try {
      const payload = {
        tier: editingRule.tier,
        credit_cost: parseInt(editingRule.credit_cost),
        insurance_required: editingRule.insurance_required,
        active: editingRule.active
      };
      
      await axios.put(`${API}/admin/lead-pricing/${encodeURIComponent(editingRule.tier)}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pricing for "${editingRule.tier}" updated`);
      setShowEditDialog(false);
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  const getTierOrder = (tier) => {
    const order = {
      "Less than $1,000": 1,
      "$1,000–$5,000": 2,
      "$5,000–$10,000": 3,
      "$10,000+": 4
    };
    return order[tier] || 0;
  };

  const sortedRules = [...rules].sort((a, b) => getTierOrder(a.tier) - getTierOrder(b.tier));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-lead-pricing">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-5 h-5 mr-2" /> Back to Admin
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="font-manrope font-bold text-xl text-[#1F2933]">Lead Pricing Rules</h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-[#1F2933]/60">
            Configure credit costs and insurance requirements for each budget tier. All lead routing and credit deductions use these values.
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-[#0FA3B1]/5 rounded-xl p-4 border border-[#0FA3B1]/20">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="w-5 h-5 text-[#0FA3B1]" />
              <span className="font-medium text-[#1F2933]">Credit Cost</span>
            </div>
            <p className="text-sm text-[#1F2933]/60">
              Number of credits deducted when a contractor unlocks a lead in this budget tier.
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-[#1F2933]">Insurance Required</span>
            </div>
            <p className="text-sm text-[#1F2933]/60">
              When enabled, only insured contractors can view and unlock leads in this tier.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Budget Tier</TableHead>
                  <TableHead className="text-center">Credit Cost</TableHead>
                  <TableHead className="text-center">Insurance Required</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRules.map((rule) => (
                  <TableRow key={rule.tier} data-testid={`rule-row-${rule.tier}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-[#0FA3B1]" />
                        <span className="font-medium">{rule.tier}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 font-bold text-[#0FA3B1]">
                        <Coins className="w-4 h-4" /> {rule.credit_cost}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {rule.insurance_required ? (
                        <Badge className="bg-amber-100 text-amber-800">
                          <Shield className="w-3 h-3 mr-1" /> Required
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">
                          Not Required
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={rule.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                        {rule.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(rule)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Pricing: {editingRule?.tier}</DialogTitle>
          </DialogHeader>
          
          {editingRule && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Credit Cost</Label>
                <Input
                  type="number"
                  value={editingRule.credit_cost}
                  onChange={(e) => setEditingRule({...editingRule, credit_cost: e.target.value})}
                  className="mt-1"
                  min="1"
                />
                <p className="text-xs text-[#1F2933]/50 mt-1">
                  Credits deducted when unlocking a lead
                </p>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
                <Checkbox
                  id="insurance-required"
                  checked={editingRule.insurance_required}
                  onCheckedChange={(checked) => setEditingRule({...editingRule, insurance_required: checked === true})}
                />
                <div>
                  <label htmlFor="insurance-required" className="text-[#1F2933] font-medium cursor-pointer">
                    Insurance Required
                  </label>
                  <p className="text-xs text-[#1F2933]/60 mt-1">
                    Only insured contractors can view and unlock leads in this tier
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#0FA3B1]" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <div className="spinner w-4 h-4 border-2 border-white/20 border-t-white"></div> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
